/**
 * What: Defines the shared research-inputs read model contract as Zod schemas, a pure
 *   normalizer/classifier, and inferred types for Study Builder and live product discovery.
 * Transport / data-flow: Produced server-side from live Shopify product metafields
 *   or the saved study product pick, validated with `shopifyProductResearchInputsSchema`. No HTTP, D1,
 *   Shopify fetch, scoring, Study Builder, apply, or writeback logic lives here.
 *
 * Algorithm or layout: Normalization is pure and bounded — one pass computes the display
 *   value, a stable complete-value fingerprint, and a deterministic classification. The
 *   classifier applies an ordered rule registry and falls back to `unknown.unmatched`. It
 *   never infers audience/respondent roles and never classifies by word count, list length,
 *   or review-shaped JSON.
 */
import * as z from 'zod';
import { isShopifyImageConcept } from './product-research-registry.js';
import { extractRichTextPlain, normalizePlainText, summarizeJson } from './plain-text.js';
import { formatMeasurementValue, MEASUREMENT_METAFIELD_TYPES } from './shopify-measurement-format.js';
import { classifyResearchInput } from './product-research-classification.js';
import { deriveMarketingClassification } from './product-research-marketing.js';
import { normalizeMatchToken } from './product-research-match-token.js';
import { metafieldSemanticRoleSchema } from './product-semantic-enrichment.js';
import { deriveRecommendedClaimQuestions, deriveRecommendedIngredientQuestions, deriveRecommendedUsageQuestions, shopifyRecommendedQuestionSchema, } from './product-research-questions.js';
export { classifyResearchInput } from './product-research-classification.js';
/**
 * Named display bounds so truncation is explicit and reviewable, never silent.
 */
export const RESEARCH_INPUT_MAX_DISPLAY_TEXT_LENGTH = 280;
export const RESEARCH_INPUT_MAX_LIST_ITEMS = 8;
const contentShapeSchema = z.enum(['text', 'list', 'number', 'boolean', 'rich_text', 'reference', 'json', 'unknown']);
const productRoleSchema = z.enum([
    'claim_or_benefit',
    'offer_or_promotion',
    'composition_or_component',
    'usage_or_compatibility',
    'product_attribute',
    'review_metadata',
    'relationship',
    'operational',
    'unknown',
]);
const studyUseSchema = z.enum([
    'product_story',
    'claim_candidate',
    'offer_candidate',
    'review_context',
    'display_only',
    'excluded',
]);
const researchRoleSchema = z.enum([
    'testable_copy',
    'segmentation',
    'attribution',
    'social_proof',
    'spec',
    'imagery',
    'operational',
    'unknown',
]);
export const SHOPIFY_MARKETING_VARIANT_TYPES = [
    'claim',
    'brand_tagline',
    'taste_sensory',
    'usage_experience',
    'ingredient_proof',
    'offer',
    'price',
    'image',
    'packaging',
    'instructions',
    'advertising',
    'description',
];
export const marketingVariantTypeSchema = z.enum(SHOPIFY_MARKETING_VARIANT_TYPES);
/** Merchant-facing test types, in the order used by the public TestFeed taxonomy. */
export const SHOPIFY_PUBLIC_TEST_TYPES = [
    'price',
    'claim',
    'image',
    'description',
    'offer',
];
export const SHOPIFY_CONCEPT_DIMENSIONS = [
    'claim',
    'brand_tagline',
    'taste_sensory',
    'usage_experience',
    'ingredient_proof',
    'offer_pricing',
    'visual_concept',
];
export const conceptDimensionSchema = z.enum(SHOPIFY_CONCEPT_DIMENSIONS);
export const shopifyProductResearchInputSchema = z.object({
    source: z.object({
        metafieldId: z.string(),
        definitionId: z.string().nullable(),
        namespace: z.string(),
        key: z.string(),
        type: z.string(),
        updatedAt: z.string().nullable(),
        valueFingerprint: z.string(),
        variantId: z.string().nullable().optional(),
        variantTitle: z.string().nullable().optional(),
    }),
    label: z.string(),
    observedValue: z.object({
        kind: z.enum(['text', 'list', 'number', 'boolean', 'reference', 'json', 'empty', 'unknown']),
        display: z.string(),
        items: z.array(z.string()).optional(),
        truncated: z.boolean(),
        unparseable: z.boolean(),
    }),
    classification: z.object({
        contentShape: contentShapeSchema,
        productRole: productRoleSchema,
        studyUse: studyUseSchema,
        researchRole: researchRoleSchema,
        marketingVariantTypes: z.array(marketingVariantTypeSchema),
        primaryMarketingVariantType: marketingVariantTypeSchema.nullable(),
        eligibleForStudy: z.boolean(),
        classificationReason: z.string(),
        ruleId: z.string(),
        recommendedQuestions: z.array(shopifyRecommendedQuestionSchema).optional(),
        suggestedRole: metafieldSemanticRoleSchema.optional(),
        isMerchantOverridden: z.boolean().optional(),
        /** True only for app data hidden from the field list (`isAppDataOnly`); the field list counts these, never shows them. */
        hidden: z.literal(true).optional(),
    }),
});
/** One value/type policy for Builder comparisons and Opportunity shortlists; semantic eligibility stays caller-owned. */
export function supportsResearchInputKind(field, type) {
    if (field.observedValue.unparseable ||
        field.observedValue.truncated ||
        !field.observedValue.display.trim() ||
        ['reference', 'boolean', 'unknown', 'empty'].includes(field.observedValue.kind))
        return false;
    if (type === 'price')
        return Number.isFinite(Number(field.observedValue.display)) && Number(field.observedValue.display) >= 0;
    if (isShopifyImageConcept(type)) {
        const value = field.observedValue.display;
        return URL.canParse(value) && new URL(value).protocol === 'https:';
    }
    return field.observedValue.kind !== 'number';
}
export const shopifyProductResearchInputsSchema = z.object({
    state: z.enum(['ready', 'partial', 'unavailable']),
    capturedAt: z.string(),
    coverage: z.object({
        fetchedCount: z.number(),
        hasMore: z.boolean(),
        caveats: z.array(z.string()),
    }),
    fields: z.array(shopifyProductResearchInputSchema),
});
/**
 * Parses a route response `researchInputs` payload through the shared Zod schema. Throws
 * on an invalid shape so the caller can render an `unavailable` read model rather than
 * silently treating a malformed payload as an empty `ready` state.
 */
export function parseShopifyProductResearchInputs(value) {
    return shopifyProductResearchInputsSchema.parse(value);
}
/**
 * Deterministic FNV-1a 32-bit hash. Pure JS so the contract runs in both the Worker and the
 * browser without a `crypto` dependency; the fingerprint only needs to be a stable identity
 * for the complete observed source value, not a cryptographic digest.
 */
function fingerprintCompleteValue(source) {
    const canonical = `${source.type}\u0001${source.value ?? ''}\u0001${source.jsonValue === undefined ? '' : JSON.stringify(source.jsonValue)}`;
    let hash = 0x811c9dc5;
    for (let i = 0; i < canonical.length; i++) {
        hash ^= canonical.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
}
function humanizeKey(key) {
    return key
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase());
}
/** The display label for a source with no metafield definition name. Variant scope is shown separately in the field list. */
function labelForSource(source) {
    if (source.key.startsWith('variant_image'))
        return 'Image';
    if (source.key === 'price' || source.key.startsWith('price:'))
        return 'Price';
    return humanizeKey(source.key);
}
function normalizeObservedValue(source, maxDisplayLength, maxListItems) {
    const rawValue = source.value;
    const type = source.type.toLowerCase();
    // An empty value is an explicit, factual state — never a research input.
    if (rawValue === null || rawValue === '') {
        return { kind: 'empty', display: '', truncated: false, unparseable: false };
    }
    if (type === 'boolean') {
        return { kind: 'boolean', display: rawValue, truncated: false, unparseable: false };
    }
    // Shopify measurement, rating and money values are JSON; show them as plain words ("500 ml", "4.5 / 5", "$24.00"). The raw
    // value (and the fingerprint computed from it) is untouched; only the human display changes.
    if (MEASUREMENT_METAFIELD_TYPES.has(type)) {
        const display = formatMeasurementValue(type, rawValue);
        if (display !== null)
            return { kind: 'number', display, truncated: false, unparseable: false };
        console.error('[shopify-product-research-inputs] measurement metafield value could not be parsed', source.key);
        const fallback = normalizePlainText(rawValue, maxDisplayLength);
        return { kind: 'unknown', display: fallback.display, truncated: fallback.truncated, unparseable: true };
    }
    if (type.startsWith('number_')) {
        return { kind: 'number', display: rawValue, truncated: false, unparseable: false };
    }
    if (type.endsWith('_reference') || type === 'metaobject_reference') {
        if (source.referenceDisplay !== undefined) {
            const text = normalizePlainText(source.referenceDisplay, maxDisplayLength);
            return { kind: 'reference', display: text.display, truncated: text.truncated, unparseable: false };
        }
        // Older picks lack resolved names. Preserve their recorded identity until the product is chosen again; no I/O here.
        const abbreviated = rawValue.length > 120 ? `${rawValue.slice(0, 117)}...` : rawValue;
        return { kind: 'reference', display: abbreviated, truncated: rawValue.length > 120, unparseable: false };
    }
    if (type.startsWith('list.')) {
        const items = parseListItems(source);
        if (items === null) {
            return { kind: 'unknown', display: rawValue, truncated: false, unparseable: true };
        }
        // A list of measurements shows each entry in plain words, the same as a single one ("500 ml, 1 L").
        const base = type.slice('list.'.length);
        const formatted = MEASUREMENT_METAFIELD_TYPES.has(base)
            ? items.map((item) => formatMeasurementValue(base, item) ?? item)
            : items;
        const truncated = formatted.length > maxListItems;
        const visible = formatted.slice(0, maxListItems);
        return {
            kind: 'list',
            display: visible.join(', '),
            items: visible,
            truncated,
            unparseable: false,
        };
    }
    if (type === 'json') {
        const summarized = summarizeJson(source, maxDisplayLength);
        if (summarized === null) {
            return { kind: 'unknown', display: rawValue, truncated: false, unparseable: true };
        }
        return {
            kind: 'json',
            display: summarized,
            truncated: summarized.endsWith('…'),
            unparseable: false,
        };
    }
    if (type === 'rich_text_field') {
        return extractRichTextPlain(source, maxDisplayLength);
    }
    if (type.endsWith('_text_field')) {
        return normalizePlainText(rawValue, maxDisplayLength);
    }
    // Unrecognized type: keep the field visible for audit rather than dropping it. The value
    // is displayable as-is, so it is not `unparseable`; truncation follows the text bound.
    const fallback = normalizePlainText(rawValue, maxDisplayLength);
    return { kind: 'unknown', display: fallback.display, truncated: fallback.truncated, unparseable: false };
}
/**
 * Parses a Shopify list metafield. List metafields expose a structured `jsonValue` array on
 * the pinned schema; the legacy `value` is a JSON string. Either may be present.
 */
function parseListItems(source) {
    const fromJson = source.jsonValue;
    if (Array.isArray(fromJson)) {
        return fromJson.map((item) => (typeof item === 'string' ? item : JSON.stringify(item)));
    }
    if (source.value && source.value.startsWith('[')) {
        try {
            const parsed = JSON.parse(source.value);
            if (Array.isArray(parsed)) {
                return parsed.map((item) => (typeof item === 'string' ? item : JSON.stringify(item)));
            }
        }
        catch (e) {
            // Fallback is a strict product requirement: keep the field visible with its raw value
            // (see normalizeObservedValue). Log so the malformed value is never masked.
            console.error('[shopify-product-research-inputs] list metafield value could not be parsed', source.key, e);
            return null;
        }
    }
    return [];
}
/**
 * Derives the base/deterministic semantic role for a research input.
 */
export function deriveBaseSemanticRole(source, baseClassification, marketingClassification) {
    if (marketingClassification.primaryMarketingVariantType) {
        if (marketingClassification.primaryMarketingVariantType === 'ingredient_proof')
            return 'ingredient';
        if (marketingClassification.primaryMarketingVariantType === 'usage_experience')
            return 'usage_routine';
        return marketingClassification.primaryMarketingVariantType;
    }
    if (baseClassification.productRole === 'claim_or_benefit')
        return 'claim';
    if (baseClassification.productRole === 'offer_or_promotion')
        return 'offer';
    if (baseClassification.productRole === 'composition_or_component')
        return 'ingredient';
    if (baseClassification.productRole === 'usage_or_compatibility')
        return 'usage_routine';
    if (baseClassification.productRole === 'review_metadata')
        return 'social_proof';
    if (baseClassification.productRole === 'operational')
        return 'unusable_internal';
    return 'technical_spec';
}
/**
 * Pure normalizer: computes the display value, complete-value fingerprint, and
 * classification in one call from only the raw Shopify metafields fields. No network or
 * persistence; emits a diagnostic console error only when a source value cannot be parsed.
 */
export function normalizeShopifyProductResearchInput(source, maxDisplayLength = RESEARCH_INPUT_MAX_DISPLAY_TEXT_LENGTH, maxListItems = RESEARCH_INPUT_MAX_LIST_ITEMS) {
    const observedValue = normalizeObservedValue(source, maxDisplayLength, maxListItems);
    const baseClassification = classifyResearchInput(source);
    // The standard Shopify product description maps to a first-class `description` semantic type
    // (see deriveMarketingClassification). It is a complete-replacement treatment, not an assertion
    // that its contents are a single claim, so base productRole/studyUse are preserved verbatim.
    const classification = source.namespace === 'shopify_product' && normalizeMatchToken(source.key) === 'description'
        ? {
            ...baseClassification,
            contentShape: 'text',
            ruleId: 'product.description',
        }
        : baseClassification;
    const marketingClassification = deriveMarketingClassification(source, observedValue, classification);
    const label = source.definition?.name?.trim() || labelForSource(source);
    const isUsage = marketingClassification.marketingVariantTypes.includes('usage_experience');
    const isIngredient = marketingClassification.marketingVariantTypes.includes('ingredient_proof');
    const isClaim = marketingClassification.marketingVariantTypes.includes('claim') || classification.studyUse === 'claim_candidate';
    const suggestedRole = deriveBaseSemanticRole(source, classification, marketingClassification);
    let recommendedQuestions;
    if (isUsage) {
        recommendedQuestions = deriveRecommendedUsageQuestions({ label, observedValue, source });
    }
    else if (isIngredient) {
        recommendedQuestions = deriveRecommendedIngredientQuestions({ label, observedValue, source });
    }
    else if (isClaim) {
        recommendedQuestions = deriveRecommendedClaimQuestions({ label, observedValue, source });
    }
    return {
        source: {
            metafieldId: source.id,
            definitionId: source.definition?.id ?? null,
            namespace: source.namespace,
            key: source.key,
            type: source.type,
            updatedAt: source.updatedAt,
            valueFingerprint: fingerprintCompleteValue(source),
            variantId: source.variantId ?? null,
            variantTitle: source.variantTitle ?? null,
        },
        label,
        observedValue,
        classification: {
            ...classification,
            ...marketingClassification,
            suggestedRole,
            ...(recommendedQuestions ? { recommendedQuestions } : {}),
        },
    };
}
//# sourceMappingURL=product-research-inputs.js.map